import React, { useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useQuery } from "react-query";

import { CCard, CCardBody, CCardHeader } from "@coreui/react";

import { nguyenVatLieuApi } from "src/1/apis/nguyen_vat_lieu.api";
import {
  CButton,
  CCheckbox,
  CNumberInput,
  CSelect,
} from "src/common/components/controls";
import { useMobileDevice } from "src/utils/hooks/viewport";

import { AddMoreRow } from "./AddMoreRow";
import { MobileSumRow } from "./MobileSumRow";
import { Row } from "./Row";

export default ({ control, materials, setValue, append, remove }) => {
  //#region Data
  const store_code = useWatch({ control, name: "store_code" });
  const checked = useWatch({ control, name: "checked" });

  const isMobile = useMobileDevice();

  const { data: materialsOptions = [] } = useQuery({
    queryKey: ["danh-sach-nvl-chua-kiem-kho", store_code, checked],
    queryFn: () =>
      nguyenVatLieuApi.getAllUncheckMaterials({
        store_code,
        date: checked,
      }),
    enabled: !!checked && !!store_code,
    select: (response) =>
      response?.data?.data?.map((e) => ({
        ...e,
        value: e?.code,
        label: e?.name,
        warePrice: e?.warePrice ?? 0,
      })),
  });
  //#endregion

  //#region Event
  const handleSelectMaterial =
    (onChangeCallback, index) => (value, selectedOption) => {
      onChangeCallback(value);
      setValue(`materials.${index}.warePrice`, selectedOption?.warePrice);
      setValue(`materials.${index}.wareUnit`, selectedOption?.wareUnit);
    };
  //#endregion

  //#region Render
  return (
    <>
      <CCard>
        <CCardHeader>
          <AddMoreRow append={append} />
        </CCardHeader>
        <CCardBody className="px-0 pt-0">
          {isMobile ? (
            <Type1
              control={control}
              materials={materials}
              setValue={setValue}
              remove={remove}
              materialsOptions={materialsOptions}
              handleSelectMaterial={handleSelectMaterial}
            />
          ) : (
            <Type2
              control={control}
              materials={materials}
              setValue={setValue}
              materialsOptions={materialsOptions}
              handleSelectMaterial={handleSelectMaterial}
              isMobile={isMobile}
            />
          )}
        </CCardBody>
      </CCard>
      {isMobile && <MobileSumRow control={control} setValue={setValue} />}
    </>
  );
  //#endregion
};

//#region Type1
const Type1 = ({
  control,
  materials,
  setValue,
  materialsOptions,
  handleSelectMaterial,
  remove,
}) => {
  return (
    <div className="flex flex-col mt-3">
      {materials.map((item, index) => (
        <React.Fragment key={item.__id}>
          <div className="flex justify-between px-4 items-center">
            <Controller
              control={control}
              name={`materials.${index}.checked`}
              render={({ field }) => <CCheckbox label="Chọn" {...field} />}
            />
            <CButton
              onClick={() => remove(index)}
              color="danger"
              className="max-w-[125px] !text-sm"
            >
              Xóa NVL này
            </CButton>
          </div>

          <div className="flex flex-col px-4">
            <Controller
              control={control}
              name={`materials.${index}.code`}
              render={({ field }) => (
                <CSelect
                  label="Mã NVL"
                  disabled
                  options={materialsOptions}
                  {...field}
                  display="code"
                />
              )}
            />
            <Controller
              control={control}
              name={`materials.${index}.code`}
              render={({ field: { onChange, ..._field } }) => (
                <CSelect
                  label="Tên NVL"
                  className="text-left"
                  options={materialsOptions}
                  {..._field}
                  onChange={handleSelectMaterial(onChange, index)}
                  select="value"
                />
              )}
            />
            <Controller
              control={control}
              name={`materials.${index}.code`}
              render={({ field }) => (
                <CSelect
                  label="Đơn vị tính"
                  options={materialsOptions}
                  {...field}
                  disabled
                  display="unit"
                />
              )}
            />
            <Controller
              control={control}
              name={`materials.${index}.wareQ`}
              render={({ field }) => (
                <CNumberInput label="Số lượng" {...field} />
              )}
            />
            <Controller
              control={control}
              name={`materials.${index}.code`}
              render={({ field }) => (
                <CSelect
                  label="Đơn giá"
                  disabled
                  options={materialsOptions}
                  {...field}
                  display="warePrice"
                />
              )}
            />
            <MaterialTotalInput
              control={control}
              setValue={setValue}
              index={index}
              label="Thành tiền"
            />
          </div>
          {index !== materials.length - 1 && <hr className="border-dashed" />}
        </React.Fragment>
      ))}
    </div>
  );
};
//#endregion

//#region Type2
const Type2 = ({
  control,
  materials,
  setValue,
  materialsOptions,
  isMobile,
}) => {
  return (
    <div className="table-responsive">
      <table className="table table-hover border c-table selectable">
        <thead>
          <tr>
            <th
              style={{
                width: "50px",
                paddingLeft: "20px",
                paddingRight: "24px",
              }}
            ></th>
            <th
              style={{
                width: "300px",
                minWidth: "300px",
                paddingRight: "24px",
              }}
            >
              Mã NVL
            </th>
            <th
              style={{
                width: "370px",
                minWidth: "370px",
                paddingRight: "24px",
                textAlign: "left",
              }}
            >
              Tên NVL
            </th>
            <th
              style={{
                width: "150px",
                minWidth: "150px",
                paddingRight: "24px",
              }}
            >
              ĐVT
            </th>
            <th
              style={{
                width: "200px",
                minWidth: "200px",
                paddingRight: "24px",
              }}
            >
              Số lượng
            </th>
            <th
              style={{
                width: "200px",
                minWidth: "200px",
                paddingRight: "24px",
                textAlign: "right",
              }}
            >
              Đơn giá
            </th>
            <th
              style={{
                width: "200px",
                minWidth: "200px",
                paddingRight: "24px",
                textAlign: "right",
              }}
            >
              Thành tiền
            </th>
          </tr>
        </thead>
        <tbody>
          {materials.map((e, index) => (
            <Row
              key={e.__id}
              rowIndex={index}
              control={control}
              setValue={setValue}
              materialsOptions={materialsOptions}
            />
          ))}
          {!isMobile && (
            <tr>
              <td colSpan={3}></td>
              <td>
                <span className="font-weight-bold">Tổng</span>
              </td>
              <td>
                <span className="font-weight-bold">
                  <TotalQuantity control={control} setValue={setValue} />
                </span>
              </td>
              <td></td>
              <td className="text-right">
                <span className="font-weight-bold pr-2">
                  <TotalPrice control={control} setValue={setValue} />
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

//#endregion

//#region MaterialTotalInput
const MaterialTotalInput = ({ control, setValue, index, label }) => {
  const wareQ = useWatch({ control, name: `materials.${index}.wareQ` });
  const warePrice = useWatch({ control, name: `materials.${index}.warePrice` });

  useEffect(() => {
    const total = wareQ * warePrice;
    setValue(`materials.${index}.total`, total);
  }, [wareQ, warePrice, index]);

  return (
    <Controller
      control={control}
      name={`materials.${index}.total`}
      render={({ field }) => (
        <CNumberInput
          label={label || undefined}
          disabled
          {...field}
          className={!label && "text-right"}
        />
      )}
    />
  );
};
//#endregion

//#region TotalQuantity
const TotalQuantity = ({ control, setValue }) => {
  const materials = useWatch({ control, name: "materials" });

  useEffect(() => {
    const quantity = materials.reduce((prev, curr) => prev + curr?.wareQ, 0);
    setValue("quantity", quantity);
  }, [materials]);

  return (
    <Controller
      control={control}
      name={`quantity`}
      render={({ field }) => <>{field.value}</>}
    />
  );
};
//#endregion

//#region TotalPrice
const TotalPrice = ({ control, setValue }) => {
  const materials = useWatch({ control, name: "materials" });

  useEffect(() => {
    const total = materials.reduce(
      (prev, curr) => prev + (curr?.warePrice || 0) * (curr?.wareQ || 0),
      0,
    );
    setValue("value", total);
  }, [materials]);

  return (
    <Controller
      control={control}
      name={`value`}
      render={({ field }) => <>{field.value?.toLocaleString("en-US")}</>}
    />
  );
};
//#endregion
